<template>
  <DoodleToolbox
    class="doodle-toolbox"
    @changeDoodleBrushSize="changeDoodleBrushSize"
    @changeDoodleColor="doodleColor = $event"
    @changeDoodleTool="doodleTool = $event"
    @closeDoodle="closeDoodle"
    @mouseenter="hoverToolbox = true"
    @mouseleave="hoverToolbox = false"
  />
  <div
    v-if="doodleTool !== 'text' && !hoverToolbox"
    id="doodle-brush"
    :class="{ eraser: doodleTool === 'eraser', rectangle: doodleTool === 'rectangle' }"
  />
  <div id="doodle-frame" />
  <DraggableContainer
    v-show="doodleTextStartPosition"
    background="'transparent'"
    class="doodle-text"
    :startPosition="doodleTextStartPosition"
    @newPosition="doodleTextNewPosition = $event"
  >
    <template #content>
      <textarea
        ref="doodleTextInput"
        v-model="doodleText"
        class="doodle-text__textarea"
        @keyup="handleInputKeyup"
      />
    </template>
  </DraggableContainer>
  <canvas v-show="showTempCanvas" id="doodle-canvas-temp" />
  <DoodleLayer
    :id="doodleLayerId"
    ref="doodleLayer"
    :doodleBrushSize="doodleBrushSize"
    :doodleColor="doodleColor"
    :doodleLayerId="doodleLayerId"
    :doodleTool="doodleTool"
    @escapeKeyup="closeDoodle"
    @mousePosition="setDoodleMousePosition"
    @showTempCanvas="showTempCanvas = $event"
    @textPosition="setDoodleTextStartPosition"
  />
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { RGBA } from '@/utils/rgba'
import DoodleLayer from '@/components/doodle/DoodleLayer.vue'
import DoodleToolbox from '@/components/doodle/DoodleToolbox.vue'
import DraggableContainer from '@/components/doodle/DraggableContainer.vue'

const emits = defineEmits<{
  (e: 'closeDoodle')
}>()

const doodleBrushSize = ref<number>(2)
const doodleColor = ref<string>('#a8071a')
const doodleLayer = ref()
const doodleLayerId = ref<string>('doodle-layer')
const doodleMousePosition = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const doodleText = ref<string>('')
const doodleTextInput = ref()
const doodleTextNewPosition = ref<{ left: string; top: string } | undefined>(undefined)
const doodleTextStartPosition = ref<{ left: string; top: string } | undefined>(undefined)
const doodleTextSvgSize = ref<{ height: number; width: number }>({ height: 0, width: 0 })
const doodleTool = ref<string>('free')
const hoverToolbox = ref<boolean>(false)
const showTempCanvas = ref<boolean>(false)

const doodleBrushSizePx = computed(() => doodleBrushSize.value + 'px')
const doodleBrushX = computed(() => doodleMousePosition.value.x - doodleBrushSize.value / 2 + 'px')
const doodleBrushY = computed(() => doodleMousePosition.value.y - doodleBrushSize.value / 2 + 'px')

// Foreign object used to hande line breaks and text wrapping in SVG
const doodleTextSvg = computed(() => {
  return `<svg xmlns="http://www.w3.org/2000/svg" height="${doodleTextSvgSize.value.height}" width="${doodleTextSvgSize.value.width}">
    <foreignObject x="0" y="0" height="${doodleTextSvgSize.value.height}" width="${doodleTextSvgSize.value.width}">
      <p xmlns="http://www.w3.org/1999/xhtml" style="${doodleTextSvgStyle.value}">
        ${doodleText.value}
      </p>
    </foreignObject>
  </svg>`
})
const doodleTextSvgStyle = computed(() => {
  // Using Arial font since it works with SVG without any additional setup
  // Regular hex color not accepted in SVG, so we convert it to RGB
  return `color: ${RGBA.fromHex(
    doodleColor.value
  )}; font-family: Arial, sans-serif; font-size: 17px; font-weight: bold; line-height: 24px; margin: 0; padding: 0 16px; white-space: normal; word-wrap: break-word;`
})

function changeDoodleBrushSize(value) {
  if (value === 'increase') {
    doodleBrushSize.value = doodleBrushSize.value + 2
  } else if (value === 'decrease' && doodleBrushSize.value > 2) {
    doodleBrushSize.value = doodleBrushSize.value - 2
  }
}
function closeDoodle() {
  doodleBrushSize.value = 2
  doodleColor.value = '#a8071a'
  doodleTool.value = 'free'
  emits('closeDoodle')
}
function closeDoodleText() {
  doodleTextNewPosition.value = undefined
  doodleTextStartPosition.value = undefined
  doodleText.value = ''
  doodleTextSvgSize.value = { height: 0, width: 0 }
}
function handleInputKeyup(event) {
  event.stopPropagation()
  if (event.key === 'Enter' && !event.shiftKey) {
    // If the textarea was dragged, use the new position, otherwise use the clicked start position
    const position = doodleTextNewPosition.value ?? doodleTextStartPosition.value
    // Let the SVG size match the textarea size
    doodleTextSvgSize.value = {
      height: doodleTextInput.value.clientHeight,
      width: doodleTextInput.value.clientWidth
    }
    // Remove newlines and replace double quotes with single quotes
    const doodleTextSvgEncoded = doodleTextSvg.value
      .replace(/(\r\n|\r|\n)/g, '<br />')
      .replace(/"/g, "'")
    // Create an image element
    const img = document.createElement('img')
    // Wait for the image to load before drawing it to the canvas
    // Adding 1 to left positioning for the border
    img.onload = () => {
      doodleLayer.value.drawImage(
        img,
        position ? parseInt(position.left) + 1 : 0,
        position ? parseInt(position.top) : 0
      )
    }
    // Load our SVG to the image and the text is drawn to the canvas
    img.src = `data:image/svg+xml,${doodleTextSvgEncoded}`
  }
  if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Escape') {
    closeDoodleText()
  }
}
function setDoodleMousePosition(value) {
  doodleMousePosition.value = value
}
async function setDoodleTextStartPosition(value: { x: number; y: number }) {
  doodleTextNewPosition.value = undefined
  doodleTextStartPosition.value = { left: value.x - 15 + 'px', top: value.y - 34 + 'px' } // Adjust for the textarea input to match mouse cursor
  await new Promise((resolve) => setTimeout(resolve, 100))
  doodleTextInput.value.focus()
}
watch(doodleTool, (newTool, oldTool) => {
  if (oldTool === 'text' && newTool !== 'text') {
    closeDoodleText()
  }
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
#doodle-brush {
  background-color: v-bind(doodleColor);
  border-radius: 50%;
  cursor: none;
  height: v-bind(doodleBrushSizePx);
  left: v-bind(doodleBrushX);
  pointer-events: none;
  position: fixed;
  top: v-bind(doodleBrushY);
  width: v-bind(doodleBrushSizePx);
  z-index: 1006;
  &.eraser {
    background-color: transparent;
    border: 1px dashed rgba(0, 0, 0, 0.8);
  }
  &.rectangle {
    border-radius: 0;
  }
}
#doodle-frame {
  border: 4px solid rgba(63, 70, 214, 0.8);
  box-sizing: border-box;
  cursor: none;
  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1008;
}
#doodle-layer,
#doodle-canvas-temp {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1000; // 200 seems to be enough, but hey... let's go for 1000
}
#doodle-canvas-temp {
  pointer-events: none;
  z-index: 1002;
}
.doodle-text {
  z-index: 1004;
  &__textarea {
    background: transparent;
    border-radius: 8px 8px 0 8px;
    border: variables.$border-width-xsmall solid variables.$color-greyscale-secondary;
    color: v-bind(doodleColor);
    font-family: Arial, sans-serif; // Match the SVG font, needs to be a font that works with the SVG
    font-size: 17px; // Match the SVG font
    font-weight: bold; // Match the SVG font
    height: 100px;
    line-height: 24px; // Match the SVG paragraph
    margin-top: -20px;
    min-height: 70px;
    min-width: 50px;
    overflow: hidden; // Hidden since scroll bar seems to conflict with the textarea size during SVG creation
    padding: calc(variables.$padding-medium + 15px) variables.$padding-xlarge
      calc(variables.$padding-medium - 1px) variables.$padding-xlarge; // Match the SVG paragraph padding (left/right)
    width: 200px;
    white-space: normal; // Match the SVG paragraph
    word-wrap: break-word; // Match the SVG paragraph
    &:focus {
      border: variables.$border-width-xsmall solid variables.$color-primary;
      outline: variables.$border-width-medium solid variables.$color-primary-light;
      // Outline workaround for iPhone/Safari
      @supports (-webkit-overflow-scrolling: touch) {
        box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
        outline: none;
        -webkit-appearance: none;
      }
      // Outline workaround for iPad/Safari
      @media not all and (min-resolution: 0.001dpcm) {
        box-shadow: 0 0 0 variables.$border-width-medium variables.$color-primary-light;
        outline: none;
        -webkit-appearance: none;
      }
    }
  }
}
.doodle-toolbox {
  right: 16px;
  top: 80px;
  z-index: 1010;
}
</style>
